/**
 * @typedef {import('./FinancesProps').default} Props
 */
import { Redirect } from 'react-router-dom';
import { translate } from '../../../App/Internationalization';
import { withBreakpoint } from '../../../shared/BreakpointProvider';
import { withProperty } from '../../../App/Homeowner/PropertyProvider';
import { withSettings } from '../../../shared/SettingProvider/SettingProvider';
import { withPropertyService } from '../../../service-container';
import AgentCard from '../../../App/Homeowner/shared/AgentCard';
import ComparableMarketCard from '../../../App/Homeowner/shared/ComparableMarketCard';
import flags from '../../../shared/Flags';
import formatAddressToString from '../../../utils/formatAddressToString';
import images from '../../../config/local/images';
import MortgageInformationCard from './MortgageInformationCard';
import paths from '../../../config/local/paths';
import PriceHistoryCard from '../../../App/Homeowner/Finances/PriceHistoryCard';
import PropertyValuation from './PropertyValuations';
import PropertyMap from '../Properties/PropertyMap';
import React from 'react';
import SeeWhatYouCanGet from '../../../App/shared/HomeValue/SeeWhatYouCanGet';
import TierGate from '../../../shared/TierGate';
import TaxHistoryCard from '../../../App/Homeowner/Finances/TaxHistoryCard';
import { toAbsoluteUrl } from '../../../utils/toAbsoluteUrl';

function shouldShowAvms(isFinances) {
	return !isFinances;
}

function shouldShowMarketValue(isMarketValuation) {
	return isMarketValuation;
}

function shouldShowComps(isFinances) {
	return !isFinances;
}

function shouldShowMortgage(isMarketValuation) {
	return !isMarketValuation;
}

function shouldShowHistory(isMarketValuation) {
	return !isMarketValuation;
}

const MAIN_COMPONENT_RULES = [
	{ key: 'avms', shouldShow: (isFinances) => shouldShowAvms(isFinances) },
	{
		key: 'marketValue',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowMarketValue(isMarketValuation),
	},
	{ key: 'comps', shouldShow: (isFinances) => shouldShowComps(isFinances) },
	{
		key: 'mortgage',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowMortgage(isMarketValuation),
	},
	{
		key: 'taxHistory',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowHistory(isMarketValuation),
	},
	{
		key: 'priceHistory',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowHistory(isMarketValuation),
	},
];

const MOBILE_COMPONENT_RULES = [
	{ key: 'avms', shouldShow: (isFinances) => shouldShowAvms(isFinances) },
	{
		key: 'marketValue',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowMarketValue(isMarketValuation),
	},
	{ key: 'agent', shouldShow: () => true },
	{ key: 'comps', shouldShow: (isFinances) => shouldShowComps(isFinances) },
	{
		key: 'mortgage',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowMortgage(isMarketValuation),
	},
	{
		key: 'taxHistory',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowHistory(isMarketValuation),
	},
	{
		key: 'priceHistory',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowHistory(isMarketValuation),
	},
];

function getComponentsByRules(
	components,
	rules,
	isFinances,
	isMarketValuation
) {
	return rules
		.filter((rule) => rule.shouldShow(isFinances, isMarketValuation))
		.map((rule) => components[rule.key]);
}

function getMainComponents(components, isFinances, isMarketValuation) {
	return getComponentsByRules(
		components,
		MAIN_COMPONENT_RULES,
		isFinances,
		isMarketValuation
	);
}

function getMobileComponents(components, isFinances, isMarketValuation) {
	return getComponentsByRules(
		components,
		MOBILE_COMPONENT_RULES,
		isFinances,
		isMarketValuation
	);
}

/** @extends {React.Component<Props>} */
class Finances extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			propertyImageSrc: null,
		};
	}

	async componentDidMount() {
		await this.setPropertyImage();
	}

	async componentDidUpdate(prevProps) {
		if (this.#getPropertyId() !== Finances.getPropertyId(prevProps)) {
			await this.setPropertyImage();
		}
	}

	/**
	 * @param {Props} props
	 * @returns {string | undefined}
	 */
	static getPropertyId(props) {
		return (
			props.match?.params?.propertyId ??
			props.property?.selectedProperty?.id
		);
	}

	/** @returns {string | undefined} */
	#getPropertyId() {
		return Finances.getPropertyId(this.props);
	}

	async setPropertyImage() {
		const propertyId = this.#getPropertyId();

		if (!propertyId) {
			this.setState({ propertyImageSrc: null });
			return;
		}

		try {
			const imagePath = await this.props.propertyService.getDefaultImage(
				propertyId
			);
			this.setState({
				propertyImageSrc: toAbsoluteUrl(
					imagePath ?? images.homeowners.properties.streetView
				),
			});
		} catch (_error) {
			this.setState({
				propertyImageSrc: images.homeowners.properties.streetView,
			});
		}
	}

	#renderPropertyCard() {
		const propertyId = this.#getPropertyId();
		const selectedProperty =
			this.props.property?.propertyList?.find(
				(property) => property.id === propertyId
			) ?? this.props.property?.selectedProperty;

		if (!selectedProperty?.address) {
			return null;
		}

		return (
			<section className="card card-property-detail card-border mb-6">
				<figure>
					<img
						alt="Property"
						className="image rounded w-full"
						src={
							this.state.propertyImageSrc ??
							images.homeowners.properties.streetView
						}
					/>
				</figure>
				<section className="card-body property-details text-start">
					<div className="property-detail-item width-full">
						<header className="property-detail-item-header">
							{'Address'}
						</header>
						<footer className="property-detail-item-footer">
							{formatAddressToString(selectedProperty.address)}
						</footer>
					</div>
				</section>
				<PropertyMap
					address={selectedProperty.address}
					className="property-map mb-0"
				/>
			</section>
		);
	}

	static renderMainSection(components, isFinances, isMarketValuation) {
		return (
			<section className="responsive-main app-block-list">
				{getMainComponents(components, isFinances, isMarketValuation)}
			</section>
		);
	}

	renderSecondarySection(components) {
		return (
			<section className="responsive-secondary app-block-list">
				{this.#renderPropertyCard()}
				{components.agent}
			</section>
		);
	}

	renderMobileView(components, isFinances, isMarketValuation, title) {
		return (
			<React.Fragment>
				<h1 className="hl-ms-4 my-3">{title}</h1>
				{this.#renderPropertyCard()}
				<section className="app-block-list">
					{getMobileComponents(
						components,
						isFinances,
						isMarketValuation
					)}
				</section>
			</React.Fragment>
		);
	}

	/**  @returns {JSX.Element} */
	render() {
		if (
			!this.props.settings.isEnabled(
				flags.homeowner.financesPage,
				'homeowner_tier'
			)
		) {
			return <Redirect to={paths.app.homeowner.dashboard} />;
		}

		const components = {
			agent: (
				<TierGate
					fallback={null}
					flag={flags.branding.homeownerFinances}
					userTier="homeowner_tier"
				>
					<AgentCard key="homeowner-dashboard-agent" />
				</TierGate>
			),
			avms: (
				<PropertyValuation
					key="homeowner-dashboard-avms"
					match={this.props.match}
				/>
			),
			comps: (
				<TierGate
					fallback={null}
					flag={flags.homeowner.comparableMarketActivity}
					userTier="homeowner_tier"
				>
					<ComparableMarketCard key="homeowner-dashboard-comps" />
				</TierGate>
			),
			marketValue: (
				<SeeWhatYouCanGet
					key="homeowner-dashboard-marketValue"
					match={this.props.match}
				/>
			),
			mortgage: (
				<TierGate
					fallback={null}
					flag={flags.homeowner.mortgageInformation}
					userTier="homeowner_tier"
				>
					<MortgageInformationCard
						key="homeowner-dashboard-mortgage"
						match={this.props.match}
					/>
				</TierGate>
			),
			priceHistory: (
				<PriceHistoryCard
					key="homeowner-dashboard-priceHistory"
					match={this.props.match}
				/>
			),
			taxHistory: (
				<TaxHistoryCard
					key="homeowner-dashboard-taxHistory"
					match={this.props.match}
				/>
			),
		};

		const title =
			this.props.contentTitle ??
			translate('homeowner.pages.finances.title');
		const isMarketValuation = this.props.mode === 'marketValuation';
		const isFinances = this.props.mode === 'finances';

		if (this.props.isBelowBreakpoint) {
			return this.renderMobileView(
				components,
				isFinances,
				isMarketValuation,
				title
			);
		}

		return (
			<React.Fragment>
				<h1 className="hl-ms-4 my-3">{title}</h1>

				<section className="app-responsive-area app-responsive-sidebar-grid">
					{Finances.renderMainSection(
						components,
						isFinances,
						isMarketValuation
					)}
					{this.renderSecondarySection(components)}
				</section>
			</React.Fragment>
		);
	}
}

export default withPropertyService(
	withBreakpoint(withSettings(withProperty(Finances)))
);
